import { keyBy, mapValues, orderBy } from 'lodash';
import { API } from '@shared/types/api';
import { matchesQuery } from './matchesQuery';
import {
  CardPaginationQuery,
  CardPaginationQueryState,
  CardPaginationQueryStateWithCards,
} from './types';
import { defaultOrder, toKey } from './util';

export interface State {
  stateByQuery: { [jsonStringifiedQuery: string]: CardPaginationQueryState };
  cardById: { [cardId: string]: API.CardFragment };
}

export interface Actions {
  fetchMore(query: CardPaginationQuery): Promise<CardPaginationQueryStateWithCards>;
  addCard(card: API.CardFragment, batch?: boolean): void;
  updateCard(card: API.CardFragment, batch?: boolean): void;
  removeCard(cardId: string, batch?: boolean): void;
}

interface ActionUpdateSection {
  type: 'updateSection';
  query: CardPaginationQuery;
  cards?: API.CardFragment[];
  queryState: Partial<CardPaginationQueryState>;
}

interface ActionAddCard {
  type: 'addCard';
  card: API.CardFragment;
}

interface ActionUpdateCard {
  type: 'updateCard';
  card: API.CardFragment;
}

interface ActionRemoveCard {
  type: 'removeCard';
  cardId: string;
}

interface ActionBatch {
  type: 'batch';
  actions: Action[];
}

export type Action =
  | ActionUpdateSection
  | ActionAddCard
  | ActionUpdateCard
  | ActionRemoveCard
  | ActionBatch;

export const initialState: State = {
  stateByQuery: {},
  cardById: {},
};

function defaultSection(query: CardPaginationQuery): CardPaginationQueryState {
  return {
    hasMore: false,
    loaded: false,
    loading: false,
    cardIds: [],
    query,
  };
}

function addCardToState(
  card: API.CardFragment,
  state: CardPaginationQueryState,
  cardById: State['cardById'],
): CardPaginationQueryState {
  const cardIds = state.cardIds.includes(card.id) ? state.cardIds : [card.id, ...state.cardIds];
  const order = [...(state.query.order || []), defaultOrder];
  return {
    ...state,
    cardIds: orderBy(
      cardIds,
      // '' instead of "null" makes null values appear first, just like in BE's SQL
      order.map(item => id => (item.orderBy ? cardById[id]?.[item.orderBy] : null) || ''),
      order.map(item => (item.orderDirection === 'ASC' ? 'asc' : 'desc')),
    ),
  };
}

function removeCardFromState(
  cardId: string,
  state: CardPaginationQueryState,
): CardPaginationQueryState {
  if (!state.cardIds.includes(cardId)) {
    return state;
  }
  return { ...state, cardIds: state.cardIds.filter(id => id !== cardId) };
}

export function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'updateSection': {
      const key = toKey(action.query);
      const section = state.stateByQuery[key] || defaultSection(action.query);
      return {
        ...state,
        cardById: { ...state.cardById, ...keyBy(action.cards, 'id') },
        stateByQuery: {
          ...state.stateByQuery,
          [key]: { ...section, ...action.queryState },
        },
      };
    }
    case 'addCard': {
      const card = action.card;
      const cardById = { ...state.cardById, [card.id]: card };
      return {
        ...state,
        cardById,
        stateByQuery: mapValues(state.stateByQuery, section => {
          if (matchesQuery(card, section.query)) {
            return addCardToState(card, section, cardById);
          }
          return section;
        }),
      };
    }
    case 'updateCard': {
      const card = action.card;
      const cardById = { ...state.cardById, [card.id]: card };
      return {
        ...state,
        cardById,
        stateByQuery: mapValues(state.stateByQuery, section => {
          if (matchesQuery(card, section.query)) {
            return addCardToState(card, section, cardById);
          } else {
            return removeCardFromState(card.id, section);
          }
        }),
      };
    }
    case 'removeCard': {
      const cardId = action.cardId;
      return {
        ...state,
        stateByQuery: mapValues(state.stateByQuery, section =>
          removeCardFromState(cardId, section),
        ),
      };
    }
    case 'batch': {
      return action.actions.reduce(reducer, state);
    }
  }
}
